import { FC } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Grid } from '@mui/material';
import { FOR_BANK_SECTIONS, FOR_BANK_SUB_SECTIONS } from 'constants/businessToolSections/forBank';

import useCheckSubSectionAccess from 'hooks/useCheckSubSectionAccess';

import { FieldTableHeader } from 'components/_basic/Form/FieldTable2/components/FieldTableHeader';
import { FieldTableContainer } from 'components/_basic/Form/FieldTableContainer';
import { FormSectionFilledCard } from 'components/_basic/FormSectionFilledCard';
import { DividerComponent } from 'components/BusinessToolForm/Sections/FinancialPlan/components/DividerComponent';
import { TableSectionTitle } from 'components/BusinessToolForm/Sections/FinancialPlan/components/TableComponents/TableSectionTitle';
import { TotalRowComponent } from 'components/BusinessToolForm/Sections/FinancialPlan/components/TableComponents/TotalRowComponent';
import { getEachRowValues } from 'components/BusinessToolForm/Sections/FinancialPlan/ProfitAndLossProjection/components/FormSections/NetSalesForm/helpers';
import {
  GLOBAL_PERIOD_NAME,
  GLOBAL_PERIOD_TABLE_HEADINGS_NAME,
} from 'components/BusinessToolForm/Sections/Global/form';

import { getPeriodColumnsNames } from 'helpers/businessTool';
import { concatWithDot } from 'helpers/string';

import { IInDirectFormTableProps } from './types';
import { useGetIndirectTotalFieldsTableHeadings } from './utils/form';
import {  useGetDirectFormTotalFields } from '../../../DirectForm/DirectFormComponents/DirectFormTable/utils/form';
import { FinancingActivities } from '../../../DirectForm/DirectFormComponents/Sections/FinancingActivities';
import { InvestingActivities } from '../../../DirectForm/DirectFormComponents/Sections/InvestingActivities';
import { directFormPrefix, FIRST_COLUMN_WIDTH, inDirectFormPrefix } from '../../../utils/form';
import { AdjustmentsForForm } from '../Sections/AdjustmentsForForm';
import { OperatingAssetsAndLiabilitiesForm } from '../Sections/OperatingAssetsAndLiabilitiesForm';

import styles from './IndirectFormTable.module.scss';

export const InDirectFormTable: FC<IInDirectFormTableProps> = ({ disabled }) => {
  const { getValues } = useFormContext();
  const { t } = useTranslation();
  const TOTAL_FIELDS = useGetDirectFormTotalFields()
  const INDIRECT_TOTAL_FIELDS = useGetIndirectTotalFieldsTableHeadings()

  const period = useWatch({ name: GLOBAL_PERIOD_NAME });

  const [netIncome, netCash] = getValues([
    concatWithDot(inDirectFormPrefix, INDIRECT_TOTAL_FIELDS.NET_INCOME_PROFIT_FOR_PERIOD.name),
    concatWithDot(inDirectFormPrefix, INDIRECT_TOTAL_FIELDS.NET_CASH_PROVIDED_OPERATING_ACTIVITIES.name),
  ]);

  const canAccess = useCheckSubSectionAccess(
    FOR_BANK_SECTIONS.FINANCIAL_PLAN,
    FOR_BANK_SUB_SECTIONS.FOR_BANK_FINANCIAL_PLAN.PROJECTED_CASH_FLOW,
  );

  const changeInCashEquivalents = getValues(
    concatWithDot(directFormPrefix, TOTAL_FIELDS.NET_INCREASE_DECREASE_CASH_EQUIVALENTS.name),
  );
  const cashInTheBeginning = getValues(
    concatWithDot(directFormPrefix, TOTAL_FIELDS.CASH_EQUIVALENTS_BEGINNING_PERIOD.name),
  );
  const cashInTheEnd = getValues(concatWithDot(directFormPrefix, TOTAL_FIELDS.CASH_EQUIVALENTS_END_PERIOD.name));

  const columnsNames = getPeriodColumnsNames(period);

  return (
    <FormSectionFilledCard>
      <FieldTableContainer>
        <Grid item xs className={styles.stickyTop}>
          <FieldTableHeader
            name={GLOBAL_PERIOD_TABLE_HEADINGS_NAME}
            disabled={disabled}
            columnsNames={columnsNames || []}
            firstColumnWidth={FIRST_COLUMN_WIDTH}
            editableColumn={true}
            firstColumnClass={styles.sticky}
          />
        </Grid>
        <Grid container direction="column" gap={4}>
          <Grid container direction="column">
            <Grid item xs>
              <Grid item xs>
                <TableSectionTitle title={t('financialPlan.projectedCashFlow.inDirect.operatingActivities.title')} />
              </Grid>
              <TotalRowComponent
                totalRowTitle={t('financialPlan.projectedCashFlow.inDirect.netIncomeProfitForPeriod')}
                name={concatWithDot(inDirectFormPrefix, INDIRECT_TOTAL_FIELDS.NET_INCOME_PROFIT_FOR_PERIOD.name)}
                columnValues={getEachRowValues([netIncome], 0, period)}
                firstColumnWidth={FIRST_COLUMN_WIDTH}
                noNeedName={true}
              />
            </Grid>
            <Grid item xs>
              <AdjustmentsForForm />
            </Grid>
            <Grid item xs>
              <OperatingAssetsAndLiabilitiesForm />
            </Grid>
            {/* <Grid item xs> // delete if client agreed
            <OtherItemsForm />
          </Grid> */}
            <Grid item xs>
              <TotalRowComponent
                totalRowTitle={t('financialPlan.projectedCashFlow.inDirect.netCashProvidedOperatingActivities')}
                name={concatWithDot(
                  inDirectFormPrefix,
                  INDIRECT_TOTAL_FIELDS.NET_CASH_PROVIDED_OPERATING_ACTIVITIES.name,
                )}
                columnValues={getEachRowValues([netCash], 0, period)}
                firstColumnWidth={FIRST_COLUMN_WIDTH}
                noNeedName={true}
              />
            </Grid>
          </Grid>
          <Grid item xs>
            <InvestingActivities disabled={!canAccess} />
          </Grid>
          <Grid item xs>
            <FinancingActivities disabled={!canAccess} />
          </Grid>
          <DividerComponent />
          <Grid item xs>
            <TotalRowComponent
              name={concatWithDot(directFormPrefix, TOTAL_FIELDS.NET_INCREASE_DECREASE_CASH_EQUIVALENTS.name)}
              columnValues={getEachRowValues([changeInCashEquivalents], 0, period)}
              firstColumnWidth={FIRST_COLUMN_WIDTH}
              totalRowTitle={TOTAL_FIELDS.NET_INCREASE_DECREASE_CASH_EQUIVALENTS.title}
              noNeedName={true}
            />
            <TotalRowComponent
              name={concatWithDot(directFormPrefix, TOTAL_FIELDS.CASH_EQUIVALENTS_BEGINNING_PERIOD.name)}
              columnValues={getEachRowValues([cashInTheBeginning], 0, period)}
              firstColumnWidth={FIRST_COLUMN_WIDTH}
              totalRowTitle={TOTAL_FIELDS.CASH_EQUIVALENTS_BEGINNING_PERIOD.title}
              noNeedName={true}
            />
            <TotalRowComponent
              name={concatWithDot(directFormPrefix, TOTAL_FIELDS.CASH_EQUIVALENTS_END_PERIOD.name)}
              columnValues={getEachRowValues([cashInTheEnd], 0, period)}
              firstColumnWidth={FIRST_COLUMN_WIDTH}
              totalRowTitle={TOTAL_FIELDS.CASH_EQUIVALENTS_END_PERIOD.title}
              noNeedName={true}
            />
          </Grid>
        </Grid>
      </FieldTableContainer>
    </FormSectionFilledCard>
  );
};
